import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/apiClient";
import { notifications } from "@mantine/notifications";
import { FORM_MUTATION_META } from "@/utils/formErrorHandler";
import type { Database } from "@/types/database";
import type {
  VitalReading,
  MedicationCriteriaRule,
  MedicationDiscontinueReason,
  AllergySeverity,
} from "@livingsyncs/types";
import { useFacilityContext } from "./useFacilityContext";
import { invalidateTaskSurfaces } from "./taskSurfaces";

export type MedicationOrder =
  Database["public"]["Tables"]["medication_orders"]["Row"] & {
    recent_administrations?: MedicationAdministration[];
    /** NC (derived by the API): controlled order approved on/after the
     * receive-first cutoff that has no baseline count yet → must be received
     * (initial count) before any dose can be administered. */
    awaiting_initial_count?: boolean;
    /** Unbounded (NOT derived from recent_administrations, which is capped at
     * 10) — true if ANY dose has ever been administered. Archive Order is
     * only valid when this is false; the DELETE route 400s otherwise. */
    has_administered_dose?: boolean;
    /** Resolved by the single-order GET (discontinued_by has no Prisma
     * relation — side lookup, same pattern as administered_by_employee).
     * Only present when status is "discontinued". Null when unresolvable. */
    discontinued_by_employee?: {
      first_name: string;
      last_name: string;
    } | null;
    /** SCRUM-218: the 30-day periodic re-review clock (distinct from
     * `reviewed_at`, which is the version-approval provenance). Null until the
     * order has been re-reviewed at least once. Already on the wire — the
     * medications GET returns the full row — but absent from the generated
     * Row type, so it is declared here alongside the other API-side fields. */
    last_reviewed_at?: string | null;
  };

export type MedicationAdministration =
  Database["public"]["Tables"]["medication_administrations"]["Row"] & {
    /** Resolved by the resident medication-detail GET (administered_by has no
     * Prisma relation — it's a side lookup, same pattern as form-submissions'
     * submitted_by enrichment). Null when unassigned/unresolvable. */
    administered_by_employee?: {
      first_name: string;
      last_name: string;
    } | null;
  };

export type MedicationOrderInsert =
  Database["public"]["Tables"]["medication_orders"]["Insert"];
export type MedicationOrderUpdate =
  Database["public"]["Tables"]["medication_orders"]["Update"];

export interface AdministerMedicationPayload {
  administration_id?: string; // Required for scheduled meds, omit for PRN
  administered_time?: string;
  dosage_given?: string;
  administration_route?: string;
  notes?: string;
  observation_notes?: string;
  adverse_reaction?: string;
  // US-083: evaluated server-side against the order's active criteria
  vital_reading?: VitalReading;
  vital_unobtainable?: boolean;
  criteria_override_reason?: string;
  // US-084 WIN-2: required when administering outside the ±1h window
  window_variance_reason?: string;
  // US-085: links the record to a guided med-pass session
  med_pass_session_id?: string;
  // Standalone (PRN) path: the staff acknowledged the active med-pass notices
  // before recording this dose (audited; the guided pass records this on the
  // session instead).
  notices_acknowledged?: boolean;
}

export interface RefuseMedicationPayload {
  administration_id: string;
  refusal_reason: string;
  notes?: string;
  // US-083 D1: optional evidence — refusals are never gated
  vital_reading?: VitalReading;
  med_pass_session_id?: string;
}

export interface HoldMedicationPayload {
  administration_id: string;
  hold_reason: string;
  notes?: string;
  // US-083: optional evidence ("criteria said hold")
  vital_reading?: VitalReading;
  med_pass_session_id?: string;
}

export interface DiscontinueMedicationPayload {
  discontinue_reason: string;
  /** Structured category — `allergy_adverse_reaction` drives the MAR badge. */
  discontinue_reason_code?: MedicationDiscontinueReason;
  /**
   * Optional structured allergy capture, persisted atomically with the
   * discontinuation server-side (only honored when `discontinue_reason_code`
   * is `allergy_adverse_reaction`). Replaces the old best-effort separate
   * allergy POST that could silently fail.
   */
  allergy?: {
    allergen: string;
    severity: AllergySeverity;
    reaction?: string | null;
  };
}

export interface MedicationsDueFilters {
  time_window?: number; // Minutes (default 60)
  include_overdue?: boolean;
}

/**
 * One row of a resident's cross-medication administration history — the flat
 * read model returned by GET /residents/:id/medications/history.
 */
export interface AdministrationHistoryRow {
  id: string;
  medicationOrderId: string;
  medicationName: string;
  orderDosage: string | null;
  scheduledTime: string;
  administeredTime: string | null;
  status: string;
  dosageGiven: string | null;
  administrationRoute: string | null;
  reason: string | null;
  refusalReason: string | null;
  holdReason: string | null;
  cancellationReason: string | null;
  windowVarianceReason: string | null;
  criteriaOverrideReason: string | null;
  notes: string | null;
  observationNotes: string | null;
  adverseReaction: string | null;
  administeredBy: string | null;
  administeredByName: string | null;
  medPassSessionId: string | null;
}

export interface AdministrationHistoryResult {
  from: string;
  to: string;
  timezone: string;
  administrations: AdministrationHistoryRow[];
}

/**
 * A resident's administration history across ALL medications over a date range.
 *
 * Distinct from `useMedications(..., includeAdministrations)`, which caps at the
 * 10 most recent rows per order (or one `on_date` day), and from
 * `useMedication`, which returns a single order's history. This is the only
 * read that answers "what happened to this resident last week".
 */
export function useMedicationHistory(
  residentId: string,
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: ["medication-history", residentId, from, to],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      const { data } = await api.get(
        `/residents/${residentId}/medications/history?${params.toString()}`,
      );
      return data.data as AdministrationHistoryResult;
    },
    enabled: !!residentId && !!from && !!to,
  });
}

/**
 * Fetch medication orders for a resident
 *
 * @param residentId - Resident ID
 * @param status - Filter by status (active, discontinued, all)
 * @param includeAdministrations - Include recent administration history
 */
export function useMedications(
  residentId: string,
  status: string = "active",
  includeAdministrations: boolean = false,
  // US-084: the API defaults to approved orders (the clinical record);
  // "pending" surfaces the review queue's resident-level view.
  reviewStatus: string = "approved",
  // Med Pass timeline: when set ("YYYY-MM-DD"), recent_administrations carries
  // that facility-local calendar day's rows (asc) instead of the recent 10 —
  // for the per-day grid. Requires includeAdministrations.
  onDate?: string,
) {
  return useQuery({
    queryKey: [
      "medications",
      residentId,
      status,
      includeAdministrations,
      reviewStatus,
      onDate ?? null,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (includeAdministrations)
        params.append("include_administrations", "true");
      if (reviewStatus) params.append("review_status", reviewStatus);
      if (onDate) params.append("on_date", onDate);

      const { data } = await api.get(
        `/residents/${residentId}/medications?${params.toString()}`,
      );
      return data.data as MedicationOrder[];
    },
    enabled: !!residentId,
  });
}

/**
 * Fetch a single medication order with full administration history
 */
export function useMedication(residentId: string, medicationId: string) {
  return useQuery({
    queryKey: ["medications", residentId, medicationId],
    queryFn: async () => {
      const { data } = await api.get(
        `/residents/${residentId}/medications/${medicationId}`,
      );
      return data.data as MedicationOrder & {
        administrations: MedicationAdministration[];
      };
    },
    enabled: !!residentId && !!medicationId,
  });
}

/**
 * Create a new medication order (manual entry)
 */
export function useCreateMedication(residentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (medication: MedicationOrderInsert) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications`,
        medication,
      );
      return data.data as MedicationOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      notifications.show({
        title: "Success",
        message: "Medication order created successfully",
        color: "green",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-082: MedicationOrderForm owns error UX via onSubmitSafe/
    // handleFormErrors — 400 details map inline onto the fields (previously
    // this hook flattened them into a toast list).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Update an existing medication order
 */
export function useUpdateMedication(residentId: string, medicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (medication: MedicationOrderUpdate) => {
      const { data } = await api.put(
        `/residents/${residentId}/medications/${medicationId}`,
        medication,
      );
      return data.data as MedicationOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({
        queryKey: ["medications", residentId, medicationId],
      });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      notifications.show({
        title: "Success",
        message: "Medication order updated successfully",
        color: "green",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-082: MedicationOrderForm owns error UX via onSubmitSafe/
    // handleFormErrors — 400 details map inline onto the fields (previously
    // this hook flattened them into a toast list).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Delete a medication order (only if no administrations recorded)
 */
export function useDeleteMedication(residentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (medicationId: string) => {
      const params = new URLSearchParams({ medicationId });
      const { data } = await api.delete(
        `/residents/${residentId}/medications?${params.toString()}`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      notifications.show({
        title: "Success",
        message: "Medication order deleted successfully",
        color: "green",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to delete medication order" },
  });
}

/**
 * SCRUM-218 — record a periodic re-review of an active order.
 *
 * Stamps `last_reviewed_at`, which is the ONLY thing that clears the
 * `medication.order_review` task. The endpoint had shipped with no caller at
 * all, so the task was unclearable from the UI and staff watched their overdue
 * list grow with work they had no way to discharge. Editing an order is
 * deliberately NOT a re-review — it does not stamp the clock.
 */
export function useRefreshMedicationReview(residentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      medicationId,
      note,
    }: {
      medicationId: string;
      note?: string;
    }) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications/${medicationId}/review/refresh`,
        { note: note?.trim() || undefined },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      // The facility worklist is a separate key, and it is now a place the
      // re-review is triggered FROM — without this the row the reviewer just
      // cleared keeps its "Review overdue" badge until a manual refetch.
      queryClient.invalidateQueries({
        queryKey: ["facilityMedicationOrders"],
      });
      // The re-review worklist is derived server-side from last_reviewed_at,
      // so every task surface must refetch or the card lingers — which is the
      // very symptom this fix exists to remove.
      invalidateTaskSurfaces(queryClient);
      notifications.show({
        title: "Re-review recorded",
        message: "The 30-day review clock has been reset for this order.",
        color: "green",
      });
    },
    meta: { errorFallback: "Failed to record the re-review" },
  });
}

/**
 * Administer a medication (PRN or scheduled)
 */
export function useAdministerMedication(
  residentId: string,
  medicationId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AdministerMedicationPayload) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications/${medicationId}/administer`,
        payload,
      );
      return data.data as MedicationAdministration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({
        queryKey: ["medications", residentId, medicationId],
      });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      // The resident-level history list is a separate query key.
      queryClient.invalidateQueries({
        queryKey: ["medication-history", residentId],
      });
      // A dose carrying observation notes or an adverse reaction now reflects
      // into the resident's care notes (medication.administered → care-note
      // handler), same as completing an appointment does. Best-effort: that
      // handler runs off the fire-and-forget event bus, so a refetch racing it
      // can miss the new note — it lands on the next natural refetch.
      queryClient.invalidateQueries({ queryKey: ["care-notes", residentId] });
      notifications.show({
        title: "Success",
        message: "Medication administered successfully",
        color: "green",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-082: AdministrationModal owns error UX via onSubmitSafe/
    // handleFormErrors — 400 details map inline onto the fields (previously
    // this hook flattened them into a toast list).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Record medication refusal
 */
export function useRefuseMedication(residentId: string, medicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: RefuseMedicationPayload) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications/${medicationId}/refuse`,
        payload,
      );
      return data.data as MedicationAdministration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({
        queryKey: ["medications", residentId, medicationId],
      });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      notifications.show({
        title: "Medication Refused",
        message: "Medication refusal recorded successfully",
        color: "yellow",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-082: AdministrationModal's refuse form owns error UX via
    // onSubmitSafe/handleFormErrors (e.g. refusal_reason min-length 400s map
    // inline). Flipped from errorFallback ATOMICALLY with that conversion.
    meta: FORM_MUTATION_META,
  });
}

/**
 * Hold a medication administration
 */
export function useHoldMedication(residentId: string, medicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: HoldMedicationPayload) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications/${medicationId}/hold`,
        payload,
      );
      return data.data as MedicationAdministration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({
        queryKey: ["medications", residentId, medicationId],
      });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      notifications.show({
        title: "Medication Held",
        message: "Medication administration held successfully",
        color: "yellow",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-082: AdministrationModal's hold form owns error UX via
    // onSubmitSafe/handleFormErrors. Flipped from errorFallback ATOMICALLY
    // with that conversion.
    meta: FORM_MUTATION_META,
  });
}

/**
 * Discontinue a medication order
 */
export function useDiscontinueMedication(
  residentId: string,
  medicationId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DiscontinueMedicationPayload) => {
      const { data } = await api.post(
        `/residents/${residentId}/medications/${medicationId}/discontinue`,
        payload,
      );
      return data.data as MedicationOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications", residentId] });
      queryClient.invalidateQueries({
        queryKey: ["medications", residentId, medicationId],
      });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      // An allergy may have been captured atomically with the discontinuation;
      // refresh the resident detail so the header allergy chips reflect it.
      queryClient.invalidateQueries({ queryKey: ["residents", residentId] });
      notifications.show({
        title: "Medication Discontinued",
        message: "Medication order discontinued successfully",
        color: "orange",
      });
      // US-153/F-134: administer/refuse/hold all set
      // medication_administrations.status, and the board card reads
      // "N of M doses charted" off exactly those statuses.
      invalidateTaskSurfaces(queryClient);
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to discontinue medication" },
  });
}

/** One row of the facility due queue (US-083: includes active criteria). */
export interface DueMedication {
  /** medication_orders.id — named `id` so modal/administer hooks compose. */
  id: string;
  administration_id: string;
  medication_name: string;
  generic_name: string | null;
  dosage: string;
  route: string;
  form: string | null;
  scheduled_time: string;
  status: "scheduled" | "overdue";
  is_prn: boolean | null;
  criteria: MedicationCriteriaRule[];
}

export interface DueResidentGroup {
  resident_id: string;
  resident_name: string;
  room_number: string | null;
  // US-085: the resident's open guided-pass session (Start vs Resume)
  open_session: { id: string; started_at: string } | null;
  medications: DueMedication[];
}

interface MedicationsDueResponse {
  residents: {
    resident: {
      id: string;
      first_name: string;
      last_name: string;
      room_number: string | null;
    };
    open_session: { id: string; started_at: string } | null;
    medications: {
      administration_id: string;
      medication_order_id: string;
      medication_name: string;
      generic_name: string | null;
      dosage: string;
      route: string;
      form: string | null;
      scheduled_time: string;
      is_overdue: boolean;
      is_prn: boolean | null;
      criteria: MedicationCriteriaRule[];
    }[];
  }[];
}

/**
 * Fetch medications due at a facility within a time window
 *
 * **Facility Context Integration:**
 * - If `facilityId` is provided, uses that value
 * - Otherwise, falls back to current facility from context
 *
 * Fixed alongside US-083: the API returns `{ residents: [...] }` with a
 * nested `resident` object and `medication_order_id` — the previous cast
 * treated `data.data` as a flat array, so the page's `Array.isArray` guard
 * never passed and the pass sheet rendered empty.
 */
export function useMedicationsDue(
  facilityId?: string,
  filters?: MedicationsDueFilters,
  // GET /facilities/:id/medications/due requires requireOnShift server-side
  // (US-052) — unlike /me/tasks's no-employee-record exemption, THIS route's
  // on-shift requirement applies to every employee, always, regardless of
  // account type. Defaults to true so every existing caller (MedPassHeader,
  // MedicationsDuePage) is unaffected — both are only ever rendered from an
  // active med-pass workflow, which already implies on-shift. Added so
  // useDesktopNotificationWatcher (polls this in the background for ANY
  // signed-in native-shell user, regardless of shift state) can skip firing
  // a request that is guaranteed to 403 while the account is between shifts.
  enabled = true,
) {
  const { currentFacilityId } = useFacilityContext();

  // Use provided facilityId or fallback to context
  const effectiveFacilityId = facilityId ?? currentFacilityId;

  return useQuery({
    queryKey: ["medicationsDue", effectiveFacilityId, filters],
    queryFn: async (): Promise<DueResidentGroup[]> => {
      if (!effectiveFacilityId) {
        throw new Error("Facility ID is required");
      }

      const params = new URLSearchParams();
      if (filters?.time_window)
        params.append("time_window", filters.time_window.toString());
      if (filters?.include_overdue) params.append("include_overdue", "true");

      const { data } = await api.get(
        `/facilities/${effectiveFacilityId}/medications/due?${params.toString()}`,
      );
      const payload = data.data as MedicationsDueResponse;
      return payload.residents.map((group) => ({
        resident_id: group.resident.id,
        resident_name:
          `${group.resident.first_name} ${group.resident.last_name}`.trim(),
        room_number: group.resident.room_number,
        open_session: group.open_session ?? null,
        medications: group.medications.map((med) => ({
          id: med.medication_order_id,
          administration_id: med.administration_id,
          medication_name: med.medication_name,
          generic_name: med.generic_name,
          dosage: med.dosage,
          route: med.route,
          form: med.form,
          scheduled_time: med.scheduled_time,
          status: med.is_overdue ? "overdue" : "scheduled",
          is_prn: med.is_prn,
          criteria: med.criteria ?? [],
        })),
      }));
    },
    enabled: enabled && !!effectiveFacilityId,
  });
}
